<template>
  <div class="locations-list">
    <!-- Loading State -->
    <div
      v-if="loading"
      class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
    </div>

    <!-- Locations List -->
    <div
      v-else-if="locations.length > 0"
      class="space-y-3">
      <div class="mb-3 text-sm text-gray-600">
        <span class="font-medium">{{ locations.length }}</span> location{{ locations.length !== 1 ? 's' : '' }} total
      </div>

      <div
        v-for="location in locations"
        :key="location.id"
        class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <h4 class="text-base font-medium text-gray-900">
              {{ location.name }}
            </h4>
            <p
              v-if="location.fullAddress"
              class="text-sm text-gray-600 mt-1">
              <i class="fas fa-map-marker-alt mr-1.5 text-gray-400" />
              {{ location.fullAddress }}
            </p>
            <p
              v-if="location.phoneNumber"
              class="text-sm text-gray-600 mt-2">
              <i class="fas fa-phone mr-1.5 text-gray-400" />
              {{ location.phoneNumber }}
            </p>
            <!-- Status badges -->
            <div class="flex items-center gap-2 mt-3">
              <span
                v-if="location.moments_count > 0"
                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                <i class="fas fa-chart-line mr-1" />
                {{ location.moments_count }} checkin{{ location.moments_count !== 1 ? 's' : '' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="text-center py-8 text-gray-500">
      <i class="fas fa-map-marker-alt text-4xl mb-3" />
      <p>No locations found for this organization.</p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'

export default {
  name: 'ResellerLocationsList',

  props: {
    organizationId: {
      type: String,
      required: true
    }
  },

  setup(props) {
    const locations = ref([])
    const loading = ref(true)

    const fetchLocations = async() => {
      try {
        loading.value = true

        const response = await fetch(`/ajax/organizations/${props.organizationId}/locations_with_gbp`, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          credentials: 'same-origin'
        })

        if (!response.ok) {
          throw new Error('Failed to fetch locations')
        }

        const data = await response.json()
        // Transform the data to match our template expectations
        locations.value = (data.locations || []).map((loc) => ({
          id: loc.id,
          name: loc.name,
          fullAddress: loc.full_address,
          phoneNumber: loc.phone_number,
          moments_count: loc.moments_count || 0
        }))
      } catch (err) {
        console.error('Error fetching locations:', err)
        locations.value = []
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      fetchLocations()
    })

    return {
      locations,
      loading
    }
  }
}
</script>

<style scoped>
.locations-list {
  @apply p-4;
}
</style>
